import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, Tooltip, useTheme } from '@ublend-npm/aulaui-core';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { HomeQuickLink } from '@core/api/homeScreen/homeQuickLinks';
import { recordQuickLinkClick } from '../../../../../../actions/home/widgetPreferences';

interface QuickLinkTileProps {
  link: HomeQuickLink;
  role: 'student' | 'educator';
  isCompact?: boolean;
}

const QuickLinkTile: React.FC<QuickLinkTileProps> = ({
  link,
  role,
  isCompact,
}) => {
  const { label, icon, href, key } = link;
  const dispatch = useDispatch();

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hrefKey = role === 'student' ? 'learner' : 'educator';
  const hrefByRole = href[hrefKey];

  const tileBg = theme.palette.background.default;
  const tileBgHover = isDark ? '#2C2733' : '#E4E1EA';
  const labelColor = theme.palette.text.secondary;

  const openLink = () => {
    dispatch(recordQuickLinkClick(key, role) as any);
    window.open(hrefByRole, '_blank', 'noopener,noreferrer');
  };

  const tileContent = (
    <Box
      onClick={openLink}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLink();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        width: '100%',
        height: { xs: 100, sm: 112, md: 112, lg: 105, xl: 112 },
        boxSizing: 'border-box',
        borderRadius: '16px',
        pt: '12px',
        pb: '12px',
        px: '12px',
        bgcolor: tileBg,
        cursor: 'pointer',
        textDecoration: 'none !important',
        transition: 'background-color 0.15s ease',
        '&, &:hover, &:visited, &:active, &:link, &:focus': {
          textDecoration: 'none !important',
        },
        '&:hover': {
          bgcolor: tileBgHover,
        },
        '&:focus-visible': {
          outline: '2px solid #694AAD',
          outlineOffset: '2px',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <img
          src={icon}
          alt=""
          width={40}
          height={40}
          style={{
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
      <Typography
        component="span"
        sx={{
          width: '100%',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '0.875rem',
          lineHeight: '140%',
          letterSpacing: '0.17px',
          textAlign: 'center',
          color: `${labelColor} !important`,
          textDecoration: 'none !important',
          borderBottom: 'none !important',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (!hrefByRole) return tileContent;

  return (
    <Tooltip
      title={
        <Box
          onClick={(e) => {
            e.stopPropagation();
            openLink();
          }}
          sx={{
            position: 'relative',
            maxWidth: '220px',
            pr: '20px',
            cursor: 'pointer',
          }}
        >
          <span>{hrefByRole}</span>
          <OpenInNewIcon
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: '14px',
              flexShrink: 0,
            }}
          />
        </Box>
      }
      arrow
      placement="bottom"
      enterTouchDelay={0}
      slotProps={{
        popper: {
          sx: {
            pointerEvents: 'auto',
            zIndex: 1300,
          },
          modifiers: [
            {
              name: 'offset',
              enabled: true,
              options: {
                offset: [0, -12],
              },
            },
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: [
                  'top',
                  'bottom-start',
                  'top-start',
                  'bottom-end',
                  'top-end',
                ],
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
              },
            },
          ],
        },
      }}
    >
      {tileContent}
    </Tooltip>
  );
};

export default QuickLinkTile;
